import React, { useState } from 'react';
import Button from '@components/Button';
import { ForgotPasswordForm } from '@helpers/Models';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Logo } from '@components/Logo';
import { ValidationService } from '@services/ValidationService';

export const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [formData, setFormData] = useState<ForgotPasswordForm>({
    email: '',
  });

  /**
   * Handles input changes for a form field, updating the form data and validating the input.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the input field.
   *
   * - Extracts the `name` and `value` from the event's target.
   * - Updates the form data state with the new value.
   * - Validates the field using `ValidationService.validateField` and updates the error state accordingly.
   *
   * @returns {void}
   *
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const normalizedValue = name === 'email' ? value.toLowerCase() : value;

    setFormData((prev) => ({ ...prev, [name]: normalizedValue }));
    // Validate the field as user types
    const error = ValidationService.validateField(
      name,
      normalizedValue,
      formData,
    );
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  /**
   * Handles the form submission for the Forgot Password form.
   *
   * @param {React.FormEvent} event - The form submission event.
   *
   * @returns {Promise<void>} A promise that resolves when the submission process completes.
   */
  const handleSubmit: (event: React.FormEvent) => Promise<void> = async (
    event,
  ) => {
    event.preventDefault();

    const { isValid, errors: validationErrors } =
      ValidationService.handleValidation(formData);
    setErrors(validationErrors);

    if (isValid) {
      try {
        const payload = { ...formData };
        await Axios.post(ApiEndpoints.FORGOT_PASSWORD, payload);
        showToastMessage(responseTexts.RESET_EMAIL_SENT, 'success');
        navigate(FrontendRoutes.LOGIN);
      } catch (error) {
        showToastMessage(getErrorMessage(error), 'error');
      }
    } else {
      showToastMessage(errorMessage.INVALID_FORM, 'error');
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo />
        <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
          Forgot Your Password?
        </h2>
        <span className="mt-2 text-sm text-center block">
          Enter your email address and we will send you instructions to reset
          your password.
        </span>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-900"
            >
              E - Mail *
            </label>
            <input
              id="email"
              name="email"
              type="text"
              value={formData.email}
              onChange={handleInputChange}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm
                             ${errors.email ? 'outline-red-500 border-red-500  focus:outline-red-500' : 'outline-gray-300 focus:outline-primary'}`}
            />
            {errors.email && (
              <p className="text-red-600 text-sm">{errors.email}</p>
            )}
          </div>

          <div>
            <Button className="mt-6" name={'Continue'}></Button>
            <p className="text-gray-800 text-sm mt-3 text-center">
              <a
                href="/login"
                className="text-primary font-semibold hover:underline ml-1"
              >
                Back to Log in
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
